import { useState, createContext, useContext, useCallback } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import axios from 'axios'
import { track } from '../utils/analytics'

const UserContext = createContext()

export function useUser() {
  return useContext(UserContext)
}

export function UserProvider({ children }) {
  const [user, setUser] = useState()
  const [isRegistered, setIsRegistered] = useState(false)
  const [error, setError] = useState()
  const isVerified = Boolean(user?.isVerified)
  const queryClient = useQueryClient()

  const { isLoading, isFetched } = useQuery({
    queryKey: ['auth'],
    queryFn: () => axios.get('/api/auth'),
    onSuccess: ({ data }) => setUser(data),
    onError: setError,
  })

  const exchangeUnsubscriptionToken = useCallback(async (token) => {
    await axios.post('/api/auth/exchange-unsubscription-token', { token })
    await queryClient.invalidateQueries(['auth'])
  }, [queryClient])

  const login = async (payload, flow = 'sign_up') => {
    const { data } = await axios.post('/api/auth/login/otp/email', payload)

    track({
      event_name: 'login_begin',
      method: 'email',
      email: payload.email.trim(),
      flow,
      purpose: 'input_email',
    })

    setIsRegistered(data.isRegistered)
    setUser({ email: data.email, isVerified: false })

    return { email: data.email }
  }

  const logOut = async () => {
    try {
      await axios.post('/api/auth/logout')
    } catch (error) {
    } finally {
      setUser()
    }
  }

  const verify = async (payload, flow = 'sign_up') => {
    await axios.post('/api/auth/verify/otp', payload)

    const email = user?.email

    track({
      event_name: isRegistered ? 'sign_up' : 'login',
      method: 'email',
      email,
      flow,
      purpose: 'input_otp',
    })

    await queryClient.invalidateQueries(['auth'])
  }

  const updateProfile = async (payload) => {
    const { data } = await axios.put('/api/user/profile', payload)
    setUser((prevState) => ({ ...prevState, ...data }))
  }

  const clearError = () => {
    setError()
  }

  const value = {
    user,
    isVerified,
    error,
    isLoading,
    isFetched,
    isRegistered,
    clearError,
    login,
    logOut,
    verify,
    exchangeUnsubscriptionToken,
    updateProfile,
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}
